<template>
    <div class="time-tracker">
        <h1>时间记录应用</h1>

        <!-- 计时器区域 -->
        <div class="timer-section">
            <div class="task-input">
                <!-- 添加TodoType下拉列表 -->
                <select v-model="selectedTodoType" class="todo-type-select">
                    <option value="">请选择分类</option>
                    <option v-for="type in todoTypes" :key="type.id" :value="type.id">
                        {{ type.title }}
                    </option>
                </select>
                <input v-model="currentTask" placeholder="输入任务内容（可留空）" list="task-history" @keyup.enter="startTimer"
                    @focus="handleInputFocus" @blur="handleInputBlur" @input="handleInput" />
                <!-- 下拉列表 -->
                <div v-if="showDropdown" class="dropdown-list">
                    <!-- baseTodo列表 -->
                    <div v-if="filteredDropdownItems.baseTodos.length > 0" class="dropdown-group">
                        <div class="dropdown-group-title">基础任务</div>
                        <div v-for="todo in filteredDropdownItems.baseTodos" :key="'base-' + todo.id"
                            class="dropdown-item base-todo-item" @mousedown="selectDropdownItem(todo)">
                            {{ todo.title }}
                        </div>
                    </div>
                    <!-- TodoItem列表 -->
                    <div v-if="filteredDropdownItems.todoItems.length > 0" class="dropdown-group">
                        <div class="dropdown-group-title">任务清单</div>
                        <div v-for="item in filteredDropdownItems.todoItems" :key="'item-' + item.id"
                            class="dropdown-item todo-item" @mousedown="selectDropdownItem(item)">
                            {{ item.title }}
                        </div>
                    </div>
                </div>
                <datalist id="task-history">
                    <option v-for="task in taskHistory" :key="task" :value="task" />
                </datalist>
                <button @click="startTimer" :disabled="isRunning">开始计时</button>
                <button @click="stopTimer" :disabled="!isRunning">结束计时</button>
            </div>

            <div class="timer-display">
                <div class="time">{{ formatTime(elapsedTime) }}</div>
                <div class="current-task" v-if="currentTaskName">当前任务: {{ currentTaskName || '未命名' }}</div>
            </div>
        </div>

        <!-- 时间记录列表 -->
        <div class="records-section">
            <h2>时间记录</h2>
            <div class="filter-controls">
                <select v-model="timeFilter" @change="filterRecords">
                    <option value="all">全部记录</option>
                    <option value="day">今天</option>
                    <option value="week">本周</option>
                    <option value="month">本月</option>
                    <option value="year">本年</option>
                </select>
            </div>

            <table class="records-table">
                <thead>
                    <tr>
                        <th>任务内容</th>
                        <th>开始时间</th>
                        <th>结束时间</th>
                        <th>持续时间</th>
                        <th>操作</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="record in filteredRecords" :key="record.id">
                        <td>{{ record.task || '未命名' }}</td>
                        <td>{{ formatDateTime(record.startTime) }}</td>
                        <td>{{ formatDateTime(record.endTime) }}</td>
                        <td>{{ formatDuration(record.duration) }}</td>
                        <td>
                            <button @click="editRecord(record)">编辑</button>
                            <button @click="deleteRecord(record.id)">删除</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- 图表统计 -->
        <div class="chart-section">
            <h2>时间统计</h2>
            <div class="chart-controls">
                <!-- 添加统计类型切换选项 -->
                <select v-model="chartStatType" @change="handleChartStatTypeChange">
                    <option value="task">按任务统计</option>
                    <option value="category">按分类统计</option>
                    <option value="time">按时间维度统计</option>
                </select>

                <!-- 时间维度选择器（仅在按时间维度统计时显示） -->
                <select v-if="chartStatType === 'time'" v-model="chartTimeDimension" @change="renderChart">
                    <option value="day">按天</option>
                    <option value="week">按周</option>
                    <option value="month">按月</option>
                    <option value="year">按年</option>
                </select>

                <!-- 图表类型选择器 -->
                <select v-model="chartType" @change="renderChart">
                    <option value="bar">柱状图</option>
                    <option value="pie">饼图</option>
                </select>

                <!-- 时间范围筛选器 -->
                <select v-model="chartTimeFilter" @change="renderChart">
                    <option value="all">全部记录</option>
                    <option value="day">今天</option>
                    <option value="week">本周</option>
                    <option value="month">本月</option>
                    <option value="year">本年</option>
                </select>
            </div>
            <div ref="chartContainer" class="chart-container"></div>
        </div>

        <!-- 编辑记录弹窗 -->
        <div v-if="showEditModal" class="modal">
            <div class="modal-content">
                <h3>编辑记录</h3>
                <form @submit.prevent="saveEditedRecord">
                    <div>
                        <label>任务内容:</label>
                        <input v-model="editingRecord.task" />
                    </div>
                    <div>
                        <label>开始时间:</label>
                        <input type="datetime-local" v-model="editingRecord.startTime" />
                    </div>
                    <div>
                        <label>结束时间:</label>
                        <input type="datetime-local" v-model="editingRecord.endTime" />
                    </div>
                    <div class="modal-actions">
                        <button type="submit">保存</button>
                        <button @click="closeEditModal">取消</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts';
import TimeTrackerService from '@/services/timeTrackerService';
import AuthService from '@/services/authService';

export default {
    name: 'TimeTracker',
    data() {
        return {
            currentTask: '',
            currentTaskName: '',
            isRunning: false,
            startTime: null,
            elapsedTime: 0,
            timerInterval: null,
            records: [],
            filteredRecords: [],
            timeFilter: 'all',
            chartType: 'bar',
            chartTimeFilter: 'all',
            chartStatType: 'task',  // 新增：统计类型 (task, category, time)
            chartTimeDimension: 'day',  // 新增：时间维度 (day, week, month, year)
            showEditModal: false,
            editingRecord: {
                id: null,
                task: '',
                startTime: '',
                endTime: ''
            },
            taskHistory: [],
            // 新增数据属性
            baseTodos: [],
            todoItems: [],
            showDropdown: false,
            userId: null,
            isDropdownSelection: false,  // 标志：表示是否来自下拉列表的选择
            todoTypes: [],
            selectedTodoType: ''
        };
    },
    computed: {
        // 计算筛选后的下拉列表项
        filteredDropdownItems() {
            // 第一步：过滤掉被baseTodo绑定的TodoItem
            const boundTodoItemIds = new Set(
                this.baseTodos
                    .filter(todo => todo.bind_todo_id)
                    .map(todo => todo.bind_todo_id)
            );

            const filteredTodoItems = this.todoItems.filter(item =>
                !boundTodoItemIds.has(item.id)
            );

            // 第二步：根据用户输入内容进行模糊匹配
            if (!this.currentTask) {
                // 如果没有输入内容，返回所有未绑定的项目
                return {
                    baseTodos: this.baseTodos,
                    todoItems: filteredTodoItems
                };
            }

            const searchTerm = this.currentTask.toLowerCase();
            const filteredBaseTodos = this.baseTodos.filter(todo =>
                todo.title.toLowerCase().includes(searchTerm)
            );

            const filteredBoundTodoItems = filteredTodoItems.filter(item =>
                item.title.toLowerCase().includes(searchTerm)
            );

            return {
                baseTodos: filteredBaseTodos,
                todoItems: filteredBoundTodoItems
            };
        }
    },
    mounted() {
        // 获取用户ID
        const user = AuthService.getCurrentUser();
        if (user) {
            this.userId = user.userid || user.id;
        }

        this.loadRecords(); // 这会调用loadTodoHistoryList来从服务器获取数据
        this.loadBaseTodos();
        this.loadTodoItems();
        this.loadTodoTypes(); // 加载TodoTypes

        // 获取最新的todoHistory记录并恢复计时
        this.restoreLastTimer();
    },
    beforeUnmount() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    },
    methods: {
        // 恢复最新的计时器状态
        async restoreLastTimer() {
            try {
                // 获取最新的todoHistory记录
                const latestHistoryResponse = await TimeTrackerService.getLatestTodoHistory();

                if (latestHistoryResponse.status === 'ok' && latestHistoryResponse.latest_history) {
                    const latestHistory = latestHistoryResponse.latest_history;

                    // 检查是否有未完成的计时任务（end_time为空）
                    if (!latestHistory.end_time) {
                        // 设置当前任务名称
                        this.currentTaskName = latestHistory.base_todo_title;

                        // 设置开始时间
                        this.startTime = new Date(latestHistory.begin_time);

                        // 设置为正在运行状态
                        this.isRunning = true;

                        // 计算已过去的时间
                        this.elapsedTime = new Date() - this.startTime;

                        // 启动计时器
                        this.timerInterval = setInterval(() => {
                            this.elapsedTime = new Date() - this.startTime;
                        }, 1000);

                        console.log('恢复计时器:', {
                            taskName: this.currentTaskName,
                            startTime: this.startTime,
                            elapsedTime: this.elapsedTime
                        });
                    }
                }
            } catch (error) {
                console.error('恢复计时器失败:', error);
            }
        },

        // 从服务器加载todoHistory列表
        async loadTodoHistoryList() {
            try {
                const response = await TimeTrackerService.getTodoHistoryList();
                if (response.status === 'ok') {
                    // 将服务器返回的数据转换为records格式
                    this.records = response.todo_histories.map(history => ({
                        id: history.id,
                        task: history.base_todo_title,
                        startTime: new Date(history.begin_time),
                        endTime: history.end_time ? new Date(history.end_time) : null,
                        duration: history.end_time ?
                            new Date(history.end_time) - new Date(history.begin_time) :
                            (history.end_time ? 0 : new Date() - new Date(history.begin_time))
                    }));

                    this.filterRecords();
                    this.renderChart();
                }
            } catch (error) {
                console.error('加载todoHistory列表失败:', error);
            }
        },

        // 加载指定日期范围的历史数据
        async loadMoreHistory(startDate, endDate) {
            try {
                const response = await TimeTrackerService.getTodoHistoryListByDate(startDate, endDate);
                if (response.status === 'ok') {
                    // 将服务器返回的数据转换为records格式并添加到现有记录中
                    const newRecords = response.todo_histories.map(history => ({
                        id: history.id,
                        task: history.base_todo_title,
                        startTime: new Date(history.begin_time),
                        endTime: history.end_time ? new Date(history.end_time) : null,
                        duration: history.end_time ?
                            new Date(history.end_time) - new Date(history.begin_time) :
                            (history.end_time ? 0 : new Date() - new Date(history.begin_time))
                    }));

                    // 合并新记录到现有记录中，避免重复
                    const existingIds = new Set(this.records.map(r => r.id));
                    const uniqueNewRecords = newRecords.filter(r => !existingIds.has(r.id));
                    this.records = [...this.records, ...uniqueNewRecords];

                    this.filterRecords();
                    this.renderChart();
                }
            } catch (error) {
                console.error('加载更多历史数据失败:', error);
            }
        },

        // 按时间维度获取统计数据
        async getStatisticsByTime(startDate, endDate, dimension) {
            try {
                const response = await TimeTrackerService.getStatisticsByTime(startDate, endDate, dimension);
                if (response.status === 'ok') {
                    return response.statistics;
                }
            } catch (error) {
                console.error('获取时间维度统计数据失败:', error);
            }
            return [];
        },

        // 按分类获取统计数据
        async getStatisticsByCategory(startDate, endDate) {
            try {
                const response = await TimeTrackerService.getStatisticsByCategory(startDate, endDate);
                console.log('getStatisticsByCategory:', response);
                if (response.status === 'ok') {
                    return response.statistics;
                }
            } catch (error) {
                console.error('获取分类统计数据失败:', error);
            }
            return [];
        },

        startTimer() {
            // 清除现有的计时器
            if (this.timerInterval) {
                clearInterval(this.timerInterval);
            }

            this.currentTaskName = this.currentTask || '未命名';
            this.startTime = new Date();
            this.isRunning = true;
            this.elapsedTime = 0;

            this.timerInterval = setInterval(() => {
                this.elapsedTime = new Date() - this.startTime;
            }, 1000);

            // 添加到任务历史
            if (this.currentTask && !this.taskHistory.includes(this.currentTask)) {
                this.taskHistory.push(this.currentTask);
                // 不再使用localStorage保存任务历史
            }

            // 清空输入框
            this.currentTask = '';

            // 只有在用户输入了任务内容且不是来自下拉列表选择时才处理与后端的交互逻辑
            if (this.currentTaskName && !this.isDropdownSelection) {
                // 处理与后端的交互逻辑
                this.handleStartTimer().then(todoHistoryId => {
                    // 如果成功创建了todoHistory，更新当前记录的ID
                    if (todoHistoryId) {
                        // 更新records中最新记录的ID
                        if (this.records.length > 0) {
                            this.records[0].id = todoHistoryId;
                            // 不再使用localStorage保存记录
                        }
                    }
                });
            }
        },

        async handleStartTimer() {
            try {
                // 检查最新的todoHistory记录的end_time
                const latestHistoryResponse = await TimeTrackerService.getLatestTodoHistory();
                console.log('latestHistoryResponse:', latestHistoryResponse);
                if (latestHistoryResponse.status === 'ok' && latestHistoryResponse.latest_history) {
                    const latestHistory = latestHistoryResponse.latest_history;
                    // 如果end_time为空，则更新它
                    if (!latestHistory.end_time) {
                        await TimeTrackerService.updateTodoHistoryEndTime(latestHistory.id, new Date().toISOString());

                        // 更新records中对应的记录
                        const recordIndex = this.records.findIndex(r => r.id === latestHistory.id);
                        if (recordIndex !== -1) {
                            this.records[recordIndex].endTime = new Date();
                            this.records[recordIndex].duration = new Date() - this.records[recordIndex].startTime;
                            // 不再使用localStorage保存记录
                            this.filterRecords();
                            this.renderChart();
                        }
                    }
                }
                console.log('currentTaskName:', this.currentTaskName);
                // 如果用户输入了任务内容，则创建新的baseTodo和todoHistory
                if (this.currentTaskName) {
                    // 首先检查是否已经存在同名的baseTodo
                    const existingBaseTodo = this.baseTodos.find(todo => todo.title === this.currentTaskName);

                    let baseTodoResponse;
                    if (existingBaseTodo) {
                        // 如果已经存在同名的baseTodo，直接使用它
                        baseTodoResponse = {
                            status: 'ok',
                            base_todo: {
                                id: existingBaseTodo.id,
                                title: existingBaseTodo.title,
                                category: existingBaseTodo.category
                            }
                        };
                    } else {
                        // 如果不存在同名的baseTodo，创建一个新的
                        baseTodoResponse = await TimeTrackerService.createBaseTodo({
                            title: this.currentTaskName,
                            category: this.currentTaskName,
                            bind_todo_id: null,
                            // 添加typeid字段
                            typeid: this.selectedTodoType || null
                        });
                    }

                    console.log('baseTodoResponse:', baseTodoResponse);
                    if (baseTodoResponse.status === 'ok') {
                        const todoHistoryResponse = await TimeTrackerService.createTodoHistory({
                            base_todo_id: baseTodoResponse.base_todo.id,
                            begin_time: this.startTime.toISOString(),
                            end_time: null
                        });
                        console.log('todoHistoryResponse:', todoHistoryResponse);

                        if (todoHistoryResponse.status === 'ok') {
                            console.log('创建todoHistory成功:', todoHistoryResponse.todo_history);
                            // 重新加载baseTodos以包含新创建的项
                            await this.loadBaseTodos();
                            // 更新records-table
                            this.updateRecordsTable(todoHistoryResponse.todo_history);
                            // 返回todoHistory ID，供stopTimer使用
                            return todoHistoryResponse.todo_history.id;
                        }
                    }
                }
            } catch (error) {
                console.error('处理开始计时失败:', error);
            }
            return null;
        },

        async stopTimer() {
            if (!this.isRunning) return;

            clearInterval(this.timerInterval);
            this.isRunning = false;

            const endTime = new Date();
            const duration = endTime - this.startTime;

            // 对于手动输入的任务，我们需要创建baseTodo和todoHistory
            let todoHistoryCreated = false;
            if (this.currentTaskName) {
                try {
                    // 检查最新的todoHistory记录的end_time
                    const latestHistoryResponse = await TimeTrackerService.getLatestTodoHistory();
                    if (latestHistoryResponse.status === 'ok' && latestHistoryResponse.latest_history) {
                        const latestHistory = latestHistoryResponse.latest_history;
                        // 如果end_time为空，则更新它
                        if (!latestHistory.end_time) {
                            await TimeTrackerService.updateTodoHistoryEndTime(latestHistory.id, new Date().toISOString());

                            // 更新records中对应的记录
                            const recordIndex = this.records.findIndex(r => r.id === latestHistory.id);
                            if (recordIndex !== -1) {
                                this.records[recordIndex].endTime = new Date();
                                this.records[recordIndex].duration = new Date() - this.records[recordIndex].startTime;

                                this.filterRecords();
                                this.renderChart();
                            }
                        }
                    }

                    // 创建新的baseTodo和todoHistory
                    // 首先检查是否已经存在同名的baseTodo
                    const existingBaseTodo = this.baseTodos.find(todo => todo.title === this.currentTaskName);

                    let baseTodoResponse;
                    if (existingBaseTodo) {
                        // 如果已经存在同名的baseTodo，直接使用它
                        baseTodoResponse = {
                            status: 'ok',
                            base_todo: {
                                id: existingBaseTodo.id,
                                title: existingBaseTodo.title,
                                category: existingBaseTodo.category
                            }
                        };
                    } else {
                        // 如果不存在同名的baseTodo，创建一个新的
                        baseTodoResponse = await TimeTrackerService.createBaseTodo({
                            title: this.currentTaskName,
                            category: this.currentTaskName,
                            bind_todo_id: null,
                            // 添加typeid字段
                            typeid: this.selectedTodoType || null
                        });
                    }

                    if (baseTodoResponse.status === 'ok') {
                        const todoHistoryResponse = await TimeTrackerService.createTodoHistory({
                            base_todo_id: baseTodoResponse.base_todo.id,
                            begin_time: this.startTime.toISOString(),
                            end_time: endTime.toISOString()
                        });

                        if (todoHistoryResponse.status === 'ok') {
                            console.log('创建todoHistory成功:', todoHistoryResponse.todo_history);
                            // 重新加载baseTodos以包含新创建的项
                            await this.loadBaseTodos();
                            // 更新records-table
                            this.updateRecordsTable(todoHistoryResponse.todo_history);
                            todoHistoryCreated = true;
                        }
                    }
                } catch (error) {
                    console.error('处理停止计时失败:', error);
                }
            }

            // 保存记录到UI
            const record = {
                id: todoHistoryCreated ? this.records[0].id : Date.now(),
                task: this.currentTaskName,
                startTime: this.startTime,
                endTime: endTime,
                duration: duration
            };

            // 如果没有创建todoHistory，则添加本地记录
            if (!todoHistoryCreated) {
                this.records.unshift(record);
                // 不再使用localStorage保存记录
            }

            this.filterRecords();
            this.renderChart();

            // 自动开始下一个任务
            this.currentTaskName = '';
            // 清除现有的计时器
            if (this.timerInterval) {
                clearInterval(this.timerInterval);
            }
            this.startTimer();
        },

        formatTime(ms) {
            const totalSeconds = Math.floor(ms / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        },

        formatDateTime(date) {
            return new Date(date).toLocaleString('zh-CN');
        },

        formatDuration(ms) {
            const totalSeconds = Math.floor(ms / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            if (hours > 0) {
                return `${hours}小时${minutes}分钟${seconds}秒`;
            } else if (minutes > 0) {
                return `${minutes}分钟${seconds}秒`;
            } else {
                return `${seconds}秒`;
            }
        },

        loadRecords() {
            // 从服务器获取todoHistory列表
            this.loadTodoHistoryList();
        },






        // 更新records-table
        updateRecordsTable(todoHistory) {
            // 检查是否已存在相同ID的记录
            const existingIndex = this.records.findIndex(r => r.id === todoHistory.id);

            if (existingIndex !== -1) {
                // 更新现有记录
                this.records[existingIndex].endTime = todoHistory.end_time ? new Date(todoHistory.end_time) : null;
                this.records[existingIndex].duration = todoHistory.end_time ?
                    new Date(todoHistory.end_time) - this.records[existingIndex].startTime :
                    this.records[existingIndex].duration;
            } else {
                // 创建一个新的记录对象并添加到records数组的开头
                const newRecord = {
                    id: todoHistory.id,
                    task: todoHistory.base_todo_title,
                    startTime: new Date(todoHistory.begin_time),
                    endTime: todoHistory.end_time ? new Date(todoHistory.end_time) : null,
                    duration: todoHistory.end_time ? new Date(todoHistory.end_time) - new Date(todoHistory.begin_time) : 0
                };

                this.records.unshift(newRecord);
            }

            this.filterRecords();
            this.renderChart();
        },

        // 处理输入框获得焦点事件
        handleInputFocus() {
            this.showDropdown = true;
        },

        // 处理输入框失去焦点事件
        handleInputBlur() {
            // 延迟隐藏下拉列表，以便用户可以点击选项
            setTimeout(() => {
                this.showDropdown = false;
            }, 200);
        },

        // 处理输入框输入事件
        handleInput() {
            // 显示下拉列表以显示筛选结果
            this.showDropdown = true;
        },

        // 选择下拉列表项
        async selectDropdownItem(item) {
            this.currentTask = item.title;
            this.showDropdown = false;

            // 设置标志，表示这是来自下拉列表的选择
            this.isDropdownSelection = true;

            // 根据项目类型处理不同的逻辑
            if (item.bind_todo_id !== undefined) {
                // baseTodo项目
                await this.handleBaseTodoSelection(item);
            } else if (item.typeid !== undefined) {
                // TodoItem项目
                await this.handleTodoItemSelection(item);
            }

            // 重置标志
            this.isDropdownSelection = false;
        },

        // 处理baseTodo选择
        async handleBaseTodoSelection(item) {
            try {
                // 检查最新的todoHistory记录的end_time
                const latestHistoryResponse = await TimeTrackerService.getLatestTodoHistory();
                if (latestHistoryResponse.status === 'ok' && latestHistoryResponse.latest_history) {
                    const latestHistory = latestHistoryResponse.latest_history;
                    // 如果end_time为空，则更新它
                    if (!latestHistory.end_time) {
                        await TimeTrackerService.updateTodoHistoryEndTime(latestHistory.id, new Date().toISOString());

                        // 更新records中对应的记录
                        const recordIndex = this.records.findIndex(r => r.id === latestHistory.id);
                        if (recordIndex !== -1) {
                            this.records[recordIndex].endTime = new Date();
                            this.records[recordIndex].duration = new Date() - this.records[recordIndex].startTime;
                            // 不再使用localStorage保存记录
                            this.filterRecords();
                            this.renderChart();
                        }
                    }
                }

                // 直接使用选中的baseTodo创建todoHistory
                const todoHistoryResponse = await TimeTrackerService.createTodoHistory({
                    base_todo_id: item.id,
                    begin_time: new Date().toISOString()
                });

                if (todoHistoryResponse.status === 'ok') {
                    console.log('创建todoHistory成功:', todoHistoryResponse.todo_history);
                    // 更新records-table
                    this.updateRecordsTable(todoHistoryResponse.todo_history);

                    // 重新开始计时
                    this.currentTaskName = item.title;
                    // 清除现有的计时器
                    if (this.timerInterval) {
                        clearInterval(this.timerInterval);
                    }
                    this.startTimer();
                }
            } catch (error) {
                console.error('处理baseTodo选择失败:', error);
            }
        },

        // 处理TodoItem选择
        async handleTodoItemSelection(item) {
            try {
                // 检查最新的todoHistory记录的end_time
                const latestHistoryResponse = await TimeTrackerService.getLatestTodoHistory();
                if (latestHistoryResponse.status === 'ok' && latestHistoryResponse.latest_history) {
                    const latestHistory = latestHistoryResponse.latest_history;
                    // 如果end_time为空，则更新它
                    if (!latestHistory.end_time) {
                        await TimeTrackerService.updateTodoHistoryEndTime(latestHistory.id, new Date().toISOString());

                        // 更新records中对应的记录
                        const recordIndex = this.records.findIndex(r => r.id === latestHistory.id);
                        if (recordIndex !== -1) {
                            this.records[recordIndex].endTime = new Date();
                            this.records[recordIndex].duration = new Date() - this.records[recordIndex].startTime;
                            // 不再使用localStorage保存记录
                            this.filterRecords();
                            this.renderChart();
                        }
                    }
                }

                // 根据TodoItem创建新的baseTodo
                const baseTodoResponse = await TimeTrackerService.createBaseTodo({
                    title: item.title,
                    category: item.category,
                    bind_todo_id: item.id
                });

                if (baseTodoResponse.status === 'ok') {
                    // 使用新创建的baseTodo创建todoHistory
                    const todoHistoryResponse = await TimeTrackerService.createTodoHistory({
                        base_todo_id: baseTodoResponse.base_todo.id,
                        begin_time: new Date().toISOString()
                    });

                    if (todoHistoryResponse.status === 'ok') {
                        console.log('创建todoHistory成功:', todoHistoryResponse.todo_history);
                        // 重新加载baseTodos以包含新创建的项
                        await this.loadBaseTodos();
                        // 更新records-table
                        this.updateRecordsTable(todoHistoryResponse.todo_history);

                        // 重新开始计时
                        this.currentTaskName = item.title;
                        // 清除现有的计时器
                        if (this.timerInterval) {
                            clearInterval(this.timerInterval);
                        }
                        this.startTimer();
                    }
                }
            } catch (error) {
                console.error('处理TodoItem选择失败:', error);
            }
        },

        // 加载baseTodos
        async loadBaseTodos() {
            try {
                const response = await TimeTrackerService.getBaseTodos();
                if (response.status === 'ok') {
                    this.baseTodos = response.base_todos;
                }
            } catch (error) {
                console.error('加载baseTodos失败:', error);
            }
        },

        // 加载todoItems
        async loadTodoItems() {
            try {
                const response = await TimeTrackerService.getTodoItems();
                if (response.status === 'ok') {
                    this.todoItems = response.todo_items;
                }
            } catch (error) {
                console.error('加载todoItems失败:', error);
            }
        },

        // 加载TodoTypes
        async loadTodoTypes() {
            try {
                const response = await TimeTrackerService.getTodoTypes();
                if (response.status === 'ok') {
                    this.todoTypes = response.todo_types;
                }
            } catch (error) {
                console.error('加载TodoTypes失败:', error);
            }
        },

        filterRecords() {
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startOfWeek = new Date(startOfDay);
            startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const startOfYear = new Date(now.getFullYear(), 0, 1);

            switch (this.timeFilter) {
                case 'day':
                    this.filteredRecords = this.records.filter(record =>
                        new Date(record.startTime) >= startOfDay
                    );
                    break;
                case 'week':
                    this.filteredRecords = this.records.filter(record =>
                        new Date(record.startTime) >= startOfWeek
                    );
                    break;
                case 'month':
                    this.filteredRecords = this.records.filter(record =>
                        new Date(record.startTime) >= startOfMonth
                    );
                    break;
                case 'year':
                    this.filteredRecords = this.records.filter(record =>
                        new Date(record.startTime) >= startOfYear
                    );
                    break;
                default:
                    this.filteredRecords = [...this.records];
            }
        },

        editRecord(record) {
            this.editingRecord = {
                id: record.id,
                task: record.task || '',
                startTime: new Date(record.startTime).toISOString().slice(0, 16),
                endTime: new Date(record.endTime).toISOString().slice(0, 16)
            };
            this.showEditModal = true;
        },

        saveEditedRecord() {
            const index = this.records.findIndex(r => r.id === this.editingRecord.id);
            if (index !== -1) {
                // 更新记录
                this.records[index].task = this.editingRecord.task;
                this.records[index].startTime = new Date(this.editingRecord.startTime);
                this.records[index].endTime = new Date(this.editingRecord.endTime);
                this.records[index].duration = this.records[index].endTime - this.records[index].startTime;


                this.filterRecords();
                this.renderChart();
                this.closeEditModal();
            }
        },

        closeEditModal() {
            this.showEditModal = false;
        },

        deleteRecord(id) {
            if (confirm('确定要删除这条记录吗？')) {
                this.records = this.records.filter(record => record.id !== id);

                this.filterRecords();
                this.renderChart();
            }
        },

        renderChart() {
            // 根据统计类型选择不同的渲染方式
            if (this.chartStatType === 'category') {
                this.renderCategoryChart();
            } else if (this.chartStatType === 'time') {
                this.renderTimeDimensionChart();
            } else {
                this.renderTaskChart();
            }
        },

        // 处理统计类型切换
        handleChartStatTypeChange() {
            // 当统计类型改变时，重新渲染图表
            this.renderChart();
        },

        // 按任务统计图表
        async renderTaskChart() {
            // 过滤图表数据
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startOfWeek = new Date(startOfDay);
            startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const startOfYear = new Date(now.getFullYear(), 0, 1);

            let chartData = [...this.records];

            switch (this.chartTimeFilter) {
                case 'day':
                    chartData = chartData.filter(record =>
                        new Date(record.startTime) >= startOfDay
                    );
                    break;
                case 'week':
                    chartData = chartData.filter(record =>
                        new Date(record.startTime) >= startOfWeek
                    );
                    break;
                case 'month':
                    chartData = chartData.filter(record =>
                        new Date(record.startTime) >= startOfMonth
                    );
                    break;
                case 'year':
                    chartData = chartData.filter(record =>
                        new Date(record.startTime) >= startOfYear
                    );
                    break;
            }

            // 按任务分组统计时间
            const taskStats = {};
            chartData.forEach(record => {
                const task = record.task || '未命名';
                if (!taskStats[task]) {
                    taskStats[task] = 0;
                }
                taskStats[task] += record.duration;
            });

            // 准备图表数据
            const taskNames = Object.keys(taskStats);
            const durations = Object.values(taskStats).map(duration => {
                // 确保duration是数值类型并转换为小时
                const durationInHours = parseFloat(duration) / (1000 * 60 * 60);
                return Math.round(durationInHours * 100) / 100;
            });

            // 调试日志
            console.log('Task chart data:', { taskNames, durations, taskStats });

            // 初始化图表
            if (!this.chart) {
                this.chart = echarts.init(this.$refs.chartContainer);
            }

            // 根据图表类型渲染
            if (this.chartType === 'bar') {
                const option = {
                    title: {
                        text: '任务时间统计（小时）'
                    },
                    tooltip: {},
                    xAxis: {
                        type: 'category',
                        data: taskNames
                    },
                    yAxis: {
                        type: 'value'
                    },
                    series: [{
                        type: 'bar',
                        data: durations,
                        itemStyle: {
                            color: '#409EFF'
                        }
                    }]
                };
                this.chart.setOption(option, true);
            } else {
                // 饼图
                const pieData = taskNames.map((name, index) => ({
                    name: name,
                    value: durations[index]
                }));

                const option = {
                    title: {
                        text: '任务时间占比',
                        left: 'center'
                    },
                    tooltip: {
                        trigger: 'item',
                        formatter: '{a} <br/>{b}: {c}小时 ({d}%)'
                    },
                    legend: {
                        orient: 'vertical',
                        left: 'left'
                    },
                    series: [
                        {
                            name: '时间占比',
                            type: 'pie',
                            radius: '50%',
                            data: pieData,
                            emphasis: {
                                itemStyle: {
                                    shadowBlur: 10,
                                    shadowOffsetX: 0,
                                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                                }
                            }
                        }
                    ]
                };
                this.chart.setOption(option, true);
            }
        },

        // 按分类统计图表
        async renderCategoryChart() {
            // 获取当前筛选的时间范围
            const now = new Date();
            let startDate, endDate;

            switch (this.chartTimeFilter) {
                case 'day': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
                    break;
                }
                case 'week': {
                    const startOfWeek = new Date(now);
                    startOfWeek.setDate(now.getDate() - now.getDay());
                    startDate = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate());
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 7);
                    endDate = new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate());
                    break;
                }
                case 'month': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                    endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                    break;
                }
                case 'year': {
                    startDate = new Date(now.getFullYear(), 0, 1);
                    endDate = new Date(now.getFullYear() + 1, 0, 1);
                    break;
                }
                default: {
                    // 默认获取最近30天的数据
                    endDate = new Date();
                    startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
                }
            }

            // 调用后端API获取分类统计数据
            try {
                const statistics = await this.getStatisticsByCategory(
                    startDate.toISOString().split('.')[0],
                    endDate.toISOString().split('.')[0]
                );

                // 准备图表数据
                const categoryNames = statistics.map(stat => stat.category || '未分类');
                const durations = statistics.map(stat => {
                    // 后端返回的duration已经是小时数，不需要再除以(1000 * 60 * 60)
                    const durationInHours = parseFloat(stat.duration);
                    // 直接保留两位小数
                    return Math.round(durationInHours * 100) / 100;
                });

                // 调试日志
                console.log('Category chart data:', { categoryNames, durations, statistics });

                // 初始化图表
                if (!this.chart) {
                    this.chart = echarts.init(this.$refs.chartContainer);
                }

                // 根据图表类型渲染
                if (this.chartType === 'bar') {
                    const option = {
                        title: {
                            text: '分类时间统计（小时）'
                        },
                        tooltip: {},
                        xAxis: {
                            type: 'category',
                            data: categoryNames
                        },
                        yAxis: {
                            type: 'value'
                        },
                        series: [{
                            type: 'bar',
                            data: durations,
                            itemStyle: {
                                color: '#409EFF'
                            }
                        }]
                    };
                    this.chart.setOption(option, true);
                } else {
                    // 饼图
                    const pieData = categoryNames.map((name, index) => ({
                        name: name,
                        value: durations[index]
                    }));

                    const option = {
                        title: {
                            text: '分类时间占比',
                            left: 'center'
                        },
                        tooltip: {
                            trigger: 'item',
                            formatter: '{a} <br/>{b}: {c}小时 ({d}%)'
                        },
                        legend: {
                            orient: 'vertical',
                            left: 'left'
                        },
                        series: [
                            {
                                name: '时间占比',
                                type: 'pie',
                                radius: '50%',
                                data: pieData,
                                emphasis: {
                                    itemStyle: {
                                        shadowBlur: 10,
                                        shadowOffsetX: 0,
                                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                                    }
                                }
                            }
                        ]
                    };
                    this.chart.setOption(option, true);
                }
            } catch (error) {
                console.error('获取分类统计数据失败:', error);
            }
        },

        // 按时间维度统计图表
        async renderTimeDimensionChart() {
            // 获取当前筛选的时间范围
            const now = new Date();
            let startDate, endDate;

            switch (this.chartTimeFilter) {
                case 'day': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
                    break;
                }
                case 'week': {
                    const startOfWeek = new Date(now);
                    startOfWeek.setDate(now.getDate() - now.getDay());
                    startDate = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate());
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 7);
                    endDate = new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate());
                    break;
                }
                case 'month': {
                    startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                    endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                    break;
                }
                case 'year': {
                    startDate = new Date(now.getFullYear(), 0, 1);
                    endDate = new Date(now.getFullYear() + 1, 0, 1);
                    break;
                }
                default: {
                    // 默认获取最近30天的数据
                    endDate = new Date();
                    startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
                }
            }

            // 调用后端API获取时间维度统计数据
            try {
                const statistics = await this.getStatisticsByTime(
                    startDate.toISOString().split('.')[0],
                    endDate.toISOString().split('.')[0],
                    this.chartTimeDimension
                );

                // 准备图表数据
                const periods = statistics.map(stat => stat.period);
                const durations = statistics.map(stat => {
                    // 后端返回的duration已经是小时数，不需要再除以(1000 * 60 * 60)
                    const durationInHours = parseFloat(stat.duration);
                    // 直接保留两位小数
                    return Math.round(durationInHours * 100) / 100;
                });

                // 调试日志
                console.log('Time dimension chart data:', { periods, durations, statistics });

                // 初始化图表
                if (!this.chart) {
                    this.chart = echarts.init(this.$refs.chartContainer);
                }

                // 根据图表类型渲染
                if (this.chartType === 'bar') {
                    const option = {
                        title: {
                            text: `按${this.getTimeDimensionLabel(this.chartTimeDimension)}时间统计（小时）`
                        },
                        tooltip: {},
                        xAxis: {
                            type: 'category',
                            data: periods
                        },
                        yAxis: {
                            type: 'value'
                        },
                        series: [{
                            type: 'bar',
                            data: durations,
                            itemStyle: {
                                color: '#409EFF'
                            }
                        }]
                    };
                    this.chart.setOption(option, true);
                } else {
                    // 饼图
                    const pieData = periods.map((period, index) => ({
                        name: period,
                        value: durations[index]
                    }));

                    const option = {
                        title: {
                            text: `按${this.getTimeDimensionLabel(this.chartTimeDimension)}时间占比`,
                            left: 'center'
                        },
                        tooltip: {
                            trigger: 'item',
                            formatter: '{a} <br/>{b}: {c}小时 ({d}%)'
                        },
                        legend: {
                            orient: 'vertical',
                            left: 'left'
                        },
                        series: [
                            {
                                name: '时间占比',
                                type: 'pie',
                                radius: '50%',
                                data: pieData,
                                emphasis: {
                                    itemStyle: {
                                        shadowBlur: 10,
                                        shadowOffsetX: 0,
                                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                                    }
                                }
                            }
                        ]
                    };
                    this.chart.setOption(option, true);
                }
            } catch (error) {
                console.error('获取时间维度统计数据失败:', error);
            }
        },

        // 获取时间维度的中文标签
        getTimeDimensionLabel(dimension) {
            const labels = {
                'day': '天',
                'week': '周',
                'month': '月',
                'year': '年'
            };
            return labels[dimension] || dimension;
        },
    }
};
</script>

<style scoped>
.time-tracker {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
}

.timer-section {
    background: #f5f5f5;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.task-input {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    align-items: center;
    position: relative;
}

.task-input input {
    flex: 1;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

/* 添加TodoType下拉列表样式 */
.todo-type-select {
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: white;
    min-width: 120px;
}

.dropdown-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-height: 300px;
    overflow-y: auto;
    margin-top: 5px;
}

.dropdown-group {
    padding: 5px 0;
}

.dropdown-group-title {
    padding: 5px 10px;
    font-weight: bold;
    color: #666;
    background-color: #f5f5f5;
}

.dropdown-item {
    padding: 8px 10px;
    cursor: pointer;
}

.dropdown-item:hover {
    background-color: #e6f7ff;
}

.base-todo-item {
    color: blue;
}

.todo-item {
    color: orange;
}

.task-input button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    background: #409EFF;
    color: white;
    cursor: pointer;
}

.task-input button:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.timer-display {
    text-align: center;
}

.time {
    font-size: 48px;
    font-weight: bold;
    color: #333;
    font-family: 'Courier New', monospace;
}

.current-task {
    font-size: 18px;
    color: #666;
    margin-top: 10px;
}

.records-section {
    background: #f5f5f5;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.filter-controls {
    margin-bottom: 15px;
}

.filter-controls select {
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.records-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
}

.records-table th,
.records-table td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

.records-table th {
    background: #409EFF;
    color: white;
}

.records-table button {
    padding: 5px 10px;
    margin-right: 5px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.records-table button:first-child {
    background: #409EFF;
    color: white;
}

.records-table button:last-child {
    background: #F56C6C;
    color: white;
}

.chart-section {
    background: #f5f5f5;
    padding: 20px;
    border-radius: 8px;
}

.chart-controls {
    margin-bottom: 15px;
    display: flex;
    gap: 10px;
}

.chart-controls select {
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.chart-container {
    width: 100%;
    height: 400px;
    background: white;
    border-radius: 4px;
}

.modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 400px;
}

.modal-content h3 {
    margin-top: 0;
}

.modal-content form div {
    margin-bottom: 15px;
}

.modal-content label {
    display: block;
    margin-bottom: 5px;
}

.modal-content input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.modal-actions button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.modal-actions button:first-child {
    background: #409EFF;
    color: white;
}

.modal-actions button:last-child {
    background: #909399;
    color: white;
}
</style>